import {
  CreditCard,
  Gift,
  LayoutDashboard,
  Layers3,
  Repeat,
  BarChart3,
  User,
  Plane,
  Wallet,
} from "lucide-react";

import React from "react";
import { NavLink, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";

const AdminSidebar = () => {
  const location = useLocation();

  const auth = useSelector((x) => x.auth.value);
  const storedAuth = JSON.parse(localStorage.getItem("auth") || "null");
  const user = auth?.token ? auth : storedAuth;
  const roleId = Number(user?.user?.role_id);

  const navItems =
    roleId === 2
      ? [
          {
            to: "/dashboard-screen",
            icon: <LayoutDashboard size={20} />,
            label: "Dashboard",
            activePaths: ["/dashboard-screen"],
          },
          {
            to: "/search",
            icon: <BarChart3 />,
            label: "Serach",
            activePaths: [
              "/search",
              "/results",
              "/deal-saved",
              "/portfolio-summary",
              "/deals-details",
            ],
          },
          {
            to: "/profile-point-wallet",
            icon: <Wallet />,
            label: "Cards",
            activePaths: ["/profile-point-wallet"],
          },
        ]
      : [
          {
            to: "/dashboard",
            icon: <LayoutDashboard size={20} />,
            label: "Dashboard",
            activePaths: ["/dashboard"],
          },
          {
            to: "/cards",
            icon: <CreditCard size={20} />,
            label: "Cards",
            activePaths: ["/cards"],
          },
          {
            to: "/card-categories",
            icon: <Layers3 size={20} />,
            label: "Categories",
            activePaths: ["/card-categories"],
          },
          {
            to: "/reward-programs",
            icon: <Gift size={20} />,
            label: "Reward",
            activePaths: ["/reward-programs"],
          },
          {
            to: "/transfer-partners",
            icon: <Repeat size={20} />,
            label: "Partners",
            activePaths: ["/transfer-partners"],
          },
        ];

  const activeIndex = navItems.findIndex((item) =>
    item.activePaths.some((path) => location.pathname.startsWith(path)),
  );

  const generatePath = (index, total) => {
    if (index === -1) return "M0,20 L400,20 L400,80 L0,80 Z";

    const width = 400;
    const height = 80;

    const sectionWidth = width / total;
    const centerX = sectionWidth * index + sectionWidth / 2;

    const curveWidth = 55;
    const dipWidth = 35;
    const dipDepth = 55;

    return `
      M 0,20 
      L ${centerX - curveWidth},20 
      C ${centerX - dipWidth},20 ${centerX - dipWidth},${dipDepth} ${centerX},${dipDepth} 
      C ${centerX + dipWidth},${dipDepth} ${centerX + dipWidth},20 ${centerX + curveWidth},20 
      L ${width},20 
      L ${width},${height} 
      L 0,${height} 
      Z
    `;
  };

  return (
    <>
      <aside className="hidden lg:flex w-64 min-h-screen bg-white dark:bg-[#0A1221] border-r border-slate-200 dark:border-slate-800 flex-col sticky top-0 h-screen">
        <div className="p-6 border-b border-slate-100 dark:border-slate-800">
          <div className="flex items-center gap-3">
            <Plane className="rotate-45 text-voyage-cyan" size={28} />

            <span className="font-black text-xl text-slate-900 dark:text-white">
              JustForPoints
            </span>
          </div>
        </div>

        <nav className="flex-1 px-4 py-5 space-y-8 overflow-y-auto custom-scrollbar">
          {roleId === 1 && (
            <>
              <div className="space-y-1">
                <SidebarLink
                  to="/dashboard"
                  icon={<LayoutDashboard size={18} />}
                  label="Dashboard"
                />
              </div>

              <SidebarSection title="Cards">
                <SidebarLink
                  to="/cards"
                  icon={<CreditCard size={18} />}
                  label="Cards"
                />

                <SidebarLink
                  to="/card-categories"
                  icon={<Layers3 size={18} />}
                  label="Card Categories"
                />

                <SidebarLink
                  to="/reward-programs"
                  icon={<Gift size={18} />}
                  label="Reward Programs"
                />

                <SidebarLink
                  to="/transfer-partners"
                  icon={<Repeat size={18} />}
                  label="Transfer Partners"
                />
              </SidebarSection>

              <SidebarSection title="Analytics">
                <SidebarLink
                  to="/search"
                  icon={<BarChart3 size={18} />}
                  label="Analytics"
                />
              </SidebarSection>
            </>
          )}

          {roleId === 2 && (
            <>
              <div className="space-y-1">
                <SidebarLink
                  to="/dashboard-screen"
                  icon={<LayoutDashboard size={18} />}
                  label="Dashboard"
                />
              </div>
              <SidebarSection title="Search">
                <SidebarLink
                  to="/search"
                  icon={<BarChart3 size={18} />}
                  label="Search"
                />

                <SidebarLink
                  to="/profile-point-wallet"
                  icon={<Wallet size={18} />}
                  label="Cards"
                />
              </SidebarSection>
            </>
          )}
        </nav>
      </aside>

      <div className="lg:hidden fixed bottom-0 left-0 right-0 z-50">
        <nav className="relative h-20 flex justify-around items-end pb-2">
          <div className="absolute inset-0 w-full h-full -z-10">
            <svg
              width="100%"
              height="100%"
              viewBox="0 0 400 80"
              preserveAspectRatio="none"
              className="fill-white dark:fill-[#0F172A] drop-shadow-[0_-10px_25px_rgba(0,0,0,0.08)]"
            >
              <path
                d={generatePath(activeIndex, navItems.length)}
                className="transition-all duration-500"
              />
            </svg>
          </div>

          {navItems?.map((item, index) => {
            const isActive = activeIndex === index;

            return (
              <NavLink
                key={item.to}
                to={item.to}
                className="relative flex flex-col items-center justify-end flex-1 h-full"
              >
                <div className="relative flex flex-col items-center justify-center w-full h-16">
                  {isActive ? (
                    <div className="absolute -top-5 w-16 h-16 bg-[#D4AF37] rounded-full border-[6px] border-[#F4F6FA] dark:border-[#020817] shadow-[0_10px_25px_rgba(249,115,22,0.35)] flex items-center justify-center transition-all duration-500">
                      <div className="text-white">
                        {React.cloneElement(item.icon, { size: 28 })}
                      </div>
                    </div>
                  ) : (
                    <div
                      className="translate-y-2 text-slate-400 dark:text-slate-500 transition-all duration-300"
                      style={{ marginBottom: "-12px" }}
                    >
                      {React.cloneElement(item.icon, { size: 22 })}
                    </div>
                  )}
                </div>

                <span
                  className={`text-[10px] font-black uppercase tracking-tight transition-all duration-300 ${
                    isActive
                      ? "text-voyage-cyan"
                      : "text-slate-400 dark:text-slate-500"
                  }`}
                >
                  {item.label}
                </span>
              </NavLink>
            );
          })}
        </nav>
      </div>
    </>
  );
};

const SidebarSection = ({ title, children }) => (
  <div className="space-y-3">
    <h3 className="px-4 text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">
      {title}
    </h3>

    <div className="space-y-1">{children}</div>
  </div>
);

const SidebarLink = ({ icon, label, to }) => {
  const location = useLocation();

  const isActive = location.pathname.startsWith(to);

  return (
    <NavLink
      to={to}
      className={`w-full flex items-center justify-between px-4 py-3 rounded-2xl transition-all group ${
        isActive
          ? "bg-[#D4AF37] text-white shadow-lg"
          : "text-slate-500 hover:bg-orange-50 dark:hover:bg-slate-800"
      }`}
    >
      <div className="flex items-center gap-3">
        {icon}

        <span className="font-bold text-sm">{label}</span>
      </div>
    </NavLink>
  );
};

export default AdminSidebar;
